/**
 * Vertical Operation AST Nodes
 *
 * Column-arithmetic block:
 *   VerticalOperationBlock → (VopRow | VopSeparator)[]
 *   VopRow → VopCell[]
 *   VopCell = digit | label | color | bold | highlight | input | empty
 */

import { z } from 'zod';
import { AST_TYPES } from '../constants';

// ═══════════════════════════════════════════════════════════════════════════
// Operator
// ═══════════════════════════════════════════════════════════════════════════

export const VOP_OPERATORS = ['+', '-', '×', '÷', '='] as const;
export type VopOperator = (typeof VOP_OPERATORS)[number];

/**
 * Normalize operator shorthand. Accepts `x`/`*` → `×`, `/` → `÷`, passes through `+`/`-`.
 */
export function normalizeVopOperator(raw: string): VopOperator {
  const map: Record<string, VopOperator> = {
    '+': '+',
    '-': '-',
    '−': '-',
    '*': '×',
    'x': '×',
    'X': '×',
    '×': '×',
    '/': '÷',
    '÷': '÷',
    '=': '=',
  };
  const op = map[raw];
  if (!op) throw new Error(`VERTICAL_OPERATION: unsupported op "${raw}"`);
  return op;
}

// ═══════════════════════════════════════════════════════════════════════════
// Cell
// ═══════════════════════════════════════════════════════════════════════════

export const VopDigitCellSchema = z.object({
  type: z.literal(AST_TYPES.VOP_CELL),
  kind: z.literal('digit'),
  value: z.string(),
});

export const VopLabelCellSchema = z.object({
  type: z.literal(AST_TYPES.VOP_CELL),
  kind: z.literal('label'),
  value: z.string(),
});

export const VopInputCellSchema = z.object({
  type: z.literal(AST_TYPES.VOP_CELL),
  kind: z.literal('input'),
  name: z.string().min(1),
  digits: z.number().int().positive().default(1),
  answer: z.union([z.string(), z.number()]).optional(),
});

export const VopEmptyCellSchema = z.object({
  type: z.literal(AST_TYPES.VOP_CELL),
  kind: z.literal('empty'),
});

export const VopColorCellSchema = z.object({
  type: z.literal(AST_TYPES.VOP_CELL),
  kind: z.literal('color'),
  value: z.string(),
  color: z.string(),
});

export const VopBoldCellSchema = z.object({
  type: z.literal(AST_TYPES.VOP_CELL),
  kind: z.literal('bold'),
  value: z.string(),
});

export const VopHighlightCellSchema = z.object({
  type: z.literal(AST_TYPES.VOP_CELL),
  kind: z.literal('highlight'),
  value: z.string(),
});

export const VopCellSchema = z.discriminatedUnion('kind', [
  VopDigitCellSchema,
  VopLabelCellSchema,
  VopColorCellSchema,
  VopBoldCellSchema,
  VopHighlightCellSchema,
  VopInputCellSchema,
  VopEmptyCellSchema,
]);

export type VopDigitCell = z.infer<typeof VopDigitCellSchema>;
export type VopLabelCell = z.infer<typeof VopLabelCellSchema>;
export type VopInputCell = z.infer<typeof VopInputCellSchema>;
export type VopEmptyCell = z.infer<typeof VopEmptyCellSchema>;
export type VopColorCell = z.infer<typeof VopColorCellSchema>;
export type VopBoldCell = z.infer<typeof VopBoldCellSchema>;
export type VopHighlightCell = z.infer<typeof VopHighlightCellSchema>;
export type VopCell = z.infer<typeof VopCellSchema>;

export function createVopDigitCell(value: string): VopDigitCell {
  return VopDigitCellSchema.parse({ type: AST_TYPES.VOP_CELL, kind: 'digit', value });
}

export function createVopInputCell(
  name: string,
  digits = 1,
  answer?: string | number
): VopInputCell {
  return VopInputCellSchema.parse({
    type: AST_TYPES.VOP_CELL,
    kind: 'input',
    name,
    digits,
    answer,
  });
}

export function createVopLabelCell(value: string): VopLabelCell {
  return VopLabelCellSchema.parse({ type: AST_TYPES.VOP_CELL, kind: 'label', value });
}

export function createVopEmptyCell(): VopEmptyCell {
  return VopEmptyCellSchema.parse({ type: AST_TYPES.VOP_CELL, kind: 'empty' });
}

export function createVopColorCell(value: string, color: string): VopColorCell {
  return VopColorCellSchema.parse({ type: AST_TYPES.VOP_CELL, kind: 'color', value, color });
}

export function createVopBoldCell(value: string): VopBoldCell {
  return VopBoldCellSchema.parse({ type: AST_TYPES.VOP_CELL, kind: 'bold', value });
}

export function createVopHighlightCell(value: string): VopHighlightCell {
  return VopHighlightCellSchema.parse({ type: AST_TYPES.VOP_CELL, kind: 'highlight', value });
}

// ═══════════════════════════════════════════════════════════════════════════
// Row
// ═══════════════════════════════════════════════════════════════════════════

export const VopRowSchema = z.object({
  type: z.literal(AST_TYPES.VOP_ROW),
  cells: z.array(VopCellSchema).min(1),
  op: z.enum(VOP_OPERATORS).optional(),
});

export type VopRow = z.infer<typeof VopRowSchema>;

export function createVopRow(cells: VopCell[], op?: VopOperator): VopRow {
  return VopRowSchema.parse({ type: AST_TYPES.VOP_ROW, cells, op });
}

// ═══════════════════════════════════════════════════════════════════════════
// Carry Row
// ═══════════════════════════════════════════════════════════════════════════

export const VopCarryRowSchema = z.object({
  type: z.literal(AST_TYPES.VOP_CARRY_ROW),
  cells: z.array(VopCellSchema).min(1),
});

export type VopCarryRow = z.infer<typeof VopCarryRowSchema>;

export function createVopCarryRow(cells: VopCell[]): VopCarryRow {
  return VopCarryRowSchema.parse({ type: AST_TYPES.VOP_CARRY_ROW, cells });
}

// ═══════════════════════════════════════════════════════════════════════════
// Separator
// ═══════════════════════════════════════════════════════════════════════════

export const VopSeparatorSchema = z.object({
  type: z.literal(AST_TYPES.VOP_SEPARATOR),
});

export type VopSeparator = z.infer<typeof VopSeparatorSchema>;

export function createVopSeparator(): VopSeparator {
  return VopSeparatorSchema.parse({ type: AST_TYPES.VOP_SEPARATOR });
}

// ═══════════════════════════════════════════════════════════════════════════
// Block
// ═══════════════════════════════════════════════════════════════════════════

const VopBlockItemSchema = z.union([VopRowSchema, VopCarryRowSchema, VopSeparatorSchema]);
export type VopBlockItem = z.infer<typeof VopBlockItemSchema>;

export const VerticalOperationBlockSchema = z.object({
  type: z.literal(AST_TYPES.VERTICAL_OPERATION_BLOCK),
  op: z.enum(VOP_OPERATORS).optional(),
  items: z.array(VopBlockItemSchema).min(1),
});

export type VerticalOperationBlock = z.infer<typeof VerticalOperationBlockSchema>;

export function createVerticalOperationBlock(
  op: VopOperator | undefined,
  items: VopBlockItem[]
): VerticalOperationBlock {
  return VerticalOperationBlockSchema.parse({
    type: AST_TYPES.VERTICAL_OPERATION_BLOCK,
    op,
    items,
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// Type Guards
// ═══════════════════════════════════════════════════════════════════════════

export function isVopCell(value: unknown): value is VopCell {
  return VopCellSchema.safeParse(value).success;
}

export function isVopRow(value: unknown): value is VopRow {
  return VopRowSchema.safeParse(value).success;
}

export function isVopCarryRow(value: unknown): value is VopCarryRow {
  return VopCarryRowSchema.safeParse(value).success;
}

export function isVopSeparator(value: unknown): value is VopSeparator {
  return VopSeparatorSchema.safeParse(value).success;
}

export function isVerticalOperationBlock(value: unknown): value is VerticalOperationBlock {
  return VerticalOperationBlockSchema.safeParse(value).success;
}
